// Import environment configuration
import { ENV } from '../config/env';

// API Configuration
export const API_BASE_URL = ENV.API_BASE_URL;

// API Endpoints
export const ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',

  // Users
  USERS: '/users',
  USER_DETAILS: (userId: string) => `/users/${userId}`,
  BAN_USER: (userId: string) => `/users/${userId}/ban`,
  UNBAN_USER: (userId: string) => `/users/${userId}/unban`,
  UPDATE_USER_ROLE: (userId: string) => `/users/${userId}/role`,
  USER_PERMISSIONS: (userId: string) => `/users/${userId}/permissions`,
  CREATE_ADMIN: '/users/admin',
  ADMIN_MANAGE: '/admin/manage',
  ADMIN_MANAGE_ME: '/admin/manage/me',
  ADMIN_MANAGE_DETAIL: (adminId: string) => `/admin/manage/${adminId}`,
  ADMIN_MANAGE_RESET_PASSWORD: (adminId: string) => `/admin/manage/${adminId}/reset-password`,

  // Wallet
  WALLET_BALANCE: (userId: string) => `/wallet/${userId}/balance`,
  CREDIT_WALLET: (userId: string) => `/wallet/${userId}/credit`,
  DEBIT_WALLET: (userId: string) => `/wallet/${userId}/debit`,
  WALLET_TRANSACTIONS: (userId: string) => `/wallet/${userId}/transactions`,

  // Transactions
  TRANSACTIONS: '/payment/transactions',
  TRANSACTION_DETAILS: (transactionId: string) => `/payment/transactions/${transactionId}`,
  UPDATE_TRANSACTION_STATUS: (transactionId: string) => `/payment/transactions/${transactionId}/status`,

  // Withdrawals
  ADMIN_WITHDRAWALS: '/payment/admin/withdrawals',
  APPROVE_WITHDRAWAL: (transactionId: string) => `/payment/admin/withdrawals/${transactionId}/approve`,
  REJECT_WITHDRAWAL: (transactionId: string) => `/payment/admin/withdrawals/${transactionId}/reject`,
  COMPLETE_WITHDRAWAL: (transactionId: string) => `/payment/admin/withdrawals/${transactionId}/complete`,

  // Support Tickets
  ADMIN_TICKETS: '/support/admin/tickets',
  TICKET_DETAILS: (ticketId: string) => `/support/admin/tickets/${ticketId}`,
  UPDATE_TICKET_STATUS: (ticketId: string) => `/support/admin/tickets/${ticketId}/status`,
  ADD_TICKET_RESPONSE: (ticketId: string) => `/support/admin/tickets/${ticketId}/response`,

  // Dashboard
  REVENUE_REPORT: '/admin/reports/revenue',
  USER_ACTIVITY_REPORT: '/admin/reports/user-activity',

  // Game Wallets
  GAME_WALLETS: '/admin/game-wallets',
  GAME_WALLET_DETAIL: (id: string) => `/admin/game-wallets/${id}`,
  GAME_WALLET_TRANSACTIONS: (id: string) => `/admin/game-wallets/${id}/transactions`,
  GAME_WALLET_SYNC: (id: string) => `/admin/game-wallets/${id}/sync`,
  GAME_WALLET_STATISTICS: '/admin/game-wallets/statistics',
  GAME_WALLET_EXPORT_RECALL: '/admin/game-wallets/export-recall',
  GAME_WALLET_RECALL_ALL_PREVIEW: '/admin/game-wallets/recall-all/preview',
  GAME_WALLET_RECALL_ALL_RUN: '/admin/game-wallets/recall-all/run',
  GAME_WALLET_RECALL_ALL_JOB: (jobId: string) => `/admin/game-wallets/recall-all/jobs/${jobId}`,
  GAME_PROVIDERS: '/admin/game-providers',
  GAME_PROVIDER_DETAIL: (id: string) => `/admin/game-providers/${id}`,

  // Full Transaction & Retry Sync
  FULL_TRANSACTION_DETAIL: (transactionId: string) => `/admin/payment-transactions/${transactionId}`,
  RETRY_SYNC: (transactionId: string) => `/admin/payment-transactions/${transactionId}/sync-game-wallet`,
  FAILED_TRANSACTIONS: '/admin/payment-transactions/failed-sync',

  // Manual Game Deposits
  MANUAL_GAME_DEPOSITS: '/admin/manual-game-deposits',
  MANUAL_GAME_DEPOSIT_DETAIL: (transactionId: string) => `/admin/manual-game-deposits/${transactionId}`,
  MANUAL_GAME_DEPOSIT_CONFIRM: (transactionId: string) => `/admin/manual-game-deposits/${transactionId}/confirm`,
  MANUAL_GAME_DEPOSIT_REJECT: (transactionId: string) => `/admin/manual-game-deposits/${transactionId}/reject`,

  // Abnormal Withdrawal Protection
  WITHDRAWAL_PROFIT_ANALYSIS: (transactionId: string) => `/payment/admin/withdrawals/${transactionId}/profit-analysis`,
  ADJUST_USER_BALANCE: (userId: string) => `/payment/admin/users/${userId}/adjust-balance`,
} as const;

// Status Colors
export const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  banned: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  awaiting_admin: 'bg-orange-100 text-orange-800',
} as const;

// Priority Colors
export const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
} as const;

// Pagination Defaults
export const PAGINATION_DEFAULTS = {
  page: 1,
  limit: 10,
} as const;

// Date Formats
export const DATE_FORMATS = {
  display: 'dd/MM/yyyy HH:mm',
  api: 'yyyy-MM-dd',
  full: 'dd/MM/yyyy HH:mm:ss',
} as const;

// Currency
export const CURRENCY = {
  symbol: '$',
  code: 'USD',
  decimals: 2,
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'admin_auth_token',
  USER_DATA: 'admin_user_data',
  THEME: 'admin_theme',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please try again.',
  UNAUTHORIZED: 'You do not have permission to access this resource.',
  FORBIDDEN: 'Access denied.',
  NOT_FOUND: 'Data not found.',
  VALIDATION_ERROR: 'The provided data is invalid.',
  SERVER_ERROR: 'Server error. Please try again later.',
  INVALID_CREDENTIALS: 'The email or verification code is incorrect.',
  /** Fallback when API does not return a message (e.g. 401 login) */
  INVALID_LOGIN: 'Invalid login credentials.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful.',
  EMAIL_CODE_SENT: 'Verification code has been sent to your email.',
  USER_BANNED: 'User banned successfully.',
  USER_UNBANNED: 'User unbanned successfully.',
  ROLE_UPDATED: 'Role updated successfully.',
  WALLET_CREDITED: 'Wallet credited successfully.',
  WALLET_DEBITED: 'Wallet debited successfully.',
  TRANSACTION_UPDATED: 'Transaction status updated successfully.',
  WITHDRAWAL_APPROVED: 'Withdrawal approved successfully.',
  WITHDRAWAL_REJECTED: 'Withdrawal rejected successfully.',
  WITHDRAWAL_COMPLETED: 'Withdrawal completed successfully.',
  TICKET_UPDATED: 'Ticket updated successfully.',
  TICKET_RESPONSE_ADDED: 'Ticket response added successfully.',
  ADMIN_CREATED: 'Admin created successfully.',
  ADMIN_UPDATED: 'Admin updated successfully.',
  ADMIN_DELETED: 'Admin deleted successfully.',
  ADMIN_PASSWORD_RESET: 'Admin password reset successfully.',
  MANUAL_DEPOSIT_CONFIRMED: 'Manual deposit confirmed successfully.',
  MANUAL_DEPOSIT_REJECTED: 'Manual deposit rejected. Funds returned to user.',
  BALANCE_ADJUSTED: 'User balance adjusted successfully.',
} as const;
